import { Type } from '@hugendubel/ws_client';

/**
 * Reduced product type. This type is used in places where not all details of the product are needed or available,
 * e.g. in search results or for display in sixpacks.
 */
export type ReducedProduct = {
    articleId: number;
    articleTarget: Link;
    attributes: ReducedProductAttributes;
    cover?: Image;
    price: ReducedProductPrice;
    productType: ReducedProductType;
    availability: ReducedProductAvailability;
    alternativeArticles: ReducedProductAlternativeArticle[];
    rating: ReducedProductRating;
    trackingToken?: string;
    deliveryInformation?: ReducedProductDeliveryInformation;
};

export type ReducedProductAttributes = {
    title: string;
    subtitle?: string;
    authors?: string;
    authorList: string[];
    coverImage?: string;
    languageCode?: string;
    publicationDate?: string;
    disruptiveBadges: Partial<Record<ProductBadgeType, string>>;
    isbnWithSeparators?: string;
    ean?: string;
};

export type ReducedProductPrice = {
    price: number;
    priceLabel?: string;
    oldPrice?: number;
    oldPriceLabel?: string;
    rebateCategory?: number;
    bonusCost?: number;
};

export type ReducedProductDeliveryInformation = {
    download: boolean;
    pickupPossible: boolean;
};

export type ReducedProductType = {
    id: number;
    name?: string;
};

export type ReducedProductAvailability = {
    number: number;
    text: string;
    currentlyAvailable: boolean;
    preorder: boolean;
};

export type ReducedProductAlternativeArticle = {
    id: number;
    price: number;
    productTypeName?: string;
};

export type ReducedProductRating = {
    ratingAverage: number;
    voteCount: number;
};

export type Link = {
    extern?: boolean;
    label?: string;
    url: string;
};

export type Image = {
    imagePath: string;
    imageWidth?: number;
    imageHeight?: number;
    alternativeText?: string;
    extern: boolean;
};

export type ProductBadgeType = 'DISCOUNT' | 'VOLUME';

const parseTarget = (articleTarget: any): Link => ({
    extern: Type.boolean(articleTarget.extern, 'ReducedProduct.articleTarget.extern'),
    label: Type.optional.string(articleTarget.label, 'ReducedProduct.articleTarget.label'),
    url: Type.string(articleTarget.url, 'ReducedProduct.articleTarget.url'),
});

const parseDisruptiveBadges = (badges: any): Partial<Record<ProductBadgeType, string>> => ({
    DISCOUNT: Type.optional.string(badges.DISCOUNT, 'ReducedProduct.disruptiveBadges.DISCOUNT'),
    VOLUME: Type.optional.string(badges.VOLUME, 'ReducedProduct.disruptiveBadges.VOLUME'),
});

const parseAttributes = (attributes: any): ReducedProductAttributes => ({
    title: Type.string(attributes.title, 'ReducedProduct.attributes.title'),
    subtitle: Type.optional.string(attributes.subtitle, 'ReducedProduct.attributes.subtitle'),
    authors: Type.optional.string(attributes.authors, 'ReducedProduct.attributes.authors'),
    authorList: attributes.authorList.map((author: any) => Type.string(author, 'ReducedProduct.attributes.authorList')),
    coverImage: Type.optional.string(attributes.coverImage, 'ReducedProduct.attributes.coverImage'),
    languageCode: Type.optional.string(attributes.languageCode, 'ReducedProduct.attributes.languageCode'),
    publicationDate: Type.optional.string(attributes.publicationDate, 'ReducedProduct.attributes.publicationDate'),
    disruptiveBadges: parseDisruptiveBadges(attributes.disruptiveBadges),
    isbnWithSeparators: Type.optional.string(
        attributes.isbnWithSeparators,
        'ReducedProduct.attributes.isbnWithSeparators'
    ),
    ean: Type.optional.string(attributes.ean, 'ReducedProduct.attributeseane'),
});

const parseCover = (cover: any): Image | undefined => {
    if (!cover) {
        return undefined;
    }

    return {
        imagePath: Type.string(cover.imagePath, 'ReducedProduct.cover.imagePath'),
        imageWidth: Type.optional.number(cover.imageWidth, 'ReducedProduct.cover.imageWidth'),
        imageHeight: Type.optional.number(cover.imageHeight, 'ReducedProduct.cover.imageHeight'),
        alternativeText: Type.optional.string(cover.alternativeText, 'ReducedProduct.cover.alternativeText'),
        extern: Type.boolean(cover.extern, 'ReducedProduct.cover.extern'),
    };
};

const parsePrice = (price: any): ReducedProductPrice => ({
    price: Type.number(price.price, 'ReducedProduct.price.price'),
    priceLabel: Type.optional.string(price.priceLabel, 'ReducedProduct.price.priceLabel'),
    oldPrice: Type.optional.number(price.oldPrice, 'ReducedProduct.price.oldPrice'),
    oldPriceLabel: Type.optional.string(price.oldPriceLabel, 'ReducedProduct.price.oldPriceLabel'),
    rebateCategory: Type.optional.number(price.rebateCategory, 'ReducedProduct.price.rebateCategory'),
    bonusCost: Type.optional.number(price.bonusCost, 'ReducedProduct.price.bonusCost'),
});

const parseProductType = (productType: any): ReducedProductType => ({
    id: Type.number(productType.id, 'ReducedProduct.productType.id'),
    name: Type.optional.string(productType.name, 'ReducedProduct.productType.name'),
});

const parseDeliveryInformation = (deliveryInformation: any): ReducedProductDeliveryInformation | undefined => {
    if (!deliveryInformation) {
        return undefined;
    }

    return {
        download: Type.boolean(deliveryInformation.download, 'ReducedProduct.deliveryInformation.download'),
        pickupPossible: Type.boolean(
            deliveryInformation.pickupPossible,
            'ReducedProduct.deliveryInformation.pickupPossible'
        ),
    };
};

const parseAvailability = (availability: any): ReducedProductAvailability => ({
    number: Type.number(availability.number, 'ReducedProduct.availability.number'),
    text: Type.string(availability.text, 'ReducedProduct.availability.text'),
    currentlyAvailable: Type.boolean(availability.currentlyAvailable, 'ReducedProduct.availability.currentlyAvailable'),
    preorder: Type.boolean(availability.preorder, 'ReducedProduct.availability.preorder'),
});

const parseAlternativeArticles = (alternativeArticles: any): ReducedProductAlternativeArticle[] =>
    alternativeArticles.map((alternativeArticle: any) => ({
        id: Type.number(alternativeArticle.id, 'ReducedProduct.alternativeArticles.id'),
        price: Type.number(alternativeArticle.price, 'ReducedProduct.alternativeArticles.price'),
        productTypeName: Type.optional.string(
            alternativeArticle.productTypeName,
            'ReducedProduct.alternativeArticles.productTypeName'
        ),
    }));

const parseRating = (rating: any): ReducedProductRating => ({
    ratingAverage: Type.number(rating.ratingAverage, 'ReducedProduct.rating.ratingAverage'),
    voteCount: Type.number(rating.voteCount, 'ReducedProduct.rating.voteCount'),
});

const parse = (json: any): ReducedProduct => ({
    articleId: Type.id(json.articleId, 'ReducedProduct.articleId'),
    articleTarget: parseTarget(json.articleTarget),
    attributes: parseAttributes(json.attributes),
    cover: parseCover(json.cover),
    price: parsePrice(json.price),
    productType: parseProductType(json.productType),
    availability: parseAvailability(json.availability),
    alternativeArticles: parseAlternativeArticles(json.alternativeArticles),
    rating: parseRating(json.rating),
    trackingToken: Type.optional.string(json.trackingToken, 'ReducedProduct.trackingToken'),
    deliveryInformation: parseDeliveryInformation(json.deliveryInformation),
});

export const ReducedProductResult = { parse };
